// services/excel-export.service.ts
import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class ExcelExportService {
  constructor() {}

  /**
   * Export des remorques avec la structure exacte du fichier d'exemple
   */
  exportTrailersToExcel(
    trailers: any[],
    fileName: string = 'export_remorques',
  ): void {
    const data: any[][] = [];

    // En-têtes
    data.push([
      'N° Remorque',
      'Transporteur',
      'Type Transport',
      'Plateforme',
      'Date Remorque',
      'Fournisseur',
      'Société',
      'Référence',
      'Date Enlèvement',
      'Volume (m³)',
      'Poids (Kg)',
      'Coût (€)',
    ]);

    // Parcourir les remorques
    trailers.forEach((trailer, index) => {
      // Ligne principale de la remorque
      data.push([
        trailer.remorqueId || '',
        trailer.transportor?.nameSociety || '',
        this.getTransportTypeLabel(trailer.transportorType),
        trailer.plateform || '',
        this.formatDate(trailer.creation_date),
        '=== COMMANDES ===',
        null,
        null,
        null,
        this.formatNumber(trailer.totalVolume, 2),
        this.formatNumber(trailer.totalWeight, 1),
        this.formatNumber(trailer.totalCost, 2),
      ]);

      // Commandes de la remorque
      if (trailer.commands && trailer.commands.length > 0) {
        trailer.commands.forEach((cmd) => {
          data.push([
            null, // N° Remorque vide
            null, // Transporteur vide
            null, // Type Transport vide
            null, // Plateforme vide
            null, // Date Remorque vide
            cmd.client_cmd?.providerName || '',
            cmd.society?.name || '',
            cmd.ref_facture_cmd || '',
            this.formatDate(cmd.pickupDate || cmd.date_creation_cmd),
            this.formatNumber(cmd.totalVolume, 3),
            this.formatNumber(cmd.totalPoids, 1),
            this.formatNumber(cmd.total_ca_cmd, 2),
          ]);
        });
      }

      // Ligne vide de séparation
      data.push([
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
      ]);
    });

    // Créer la feuille Excel
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Ajuster les largeurs de colonnes
    worksheet['!cols'] = [
      { wch: 15 }, // N° Remorque
      { wch: 20 }, // Transporteur
      { wch: 15 }, // Type Transport
      { wch: 15 }, // Plateforme
      { wch: 15 }, // Date Remorque
      { wch: 25 }, // Fournisseur
      { wch: 20 }, // Société
      { wch: 20 }, // Référence
      { wch: 15 }, // Date Enlèvement
      { wch: 12 }, // Volume
      { wch: 12 }, // Poids
      { wch: 12 }, // Coût
    ];

    // Créer le classeur
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Remorques');

    // Générer le fichier
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const dateStr = this.getCurrentDate();
    saveAs(blob, `${fileName}_${dateStr}.xlsx`);
  }

  /**
   * Export avec filtrage par date et site
   */
  exportTrailersWithFilter(
    trailers: any[],
    startDate?: Date,
    site?: string,
    fileName: string = 'export_remorques',
  ): void {
    let filteredTrailers = trailers;

    // Filtrer par date de départ si spécifiée
    if (startDate) {
      filteredTrailers = filteredTrailers.filter((trailer) => {
        const trailerDate = new Date(trailer.creation_date);
        return trailerDate >= startDate;
      });
    }

    // Filtrer par site si spécifié
    if (site && site !== 'Tous') {
      filteredTrailers = filteredTrailers.filter(
        (trailer) => trailer.plateform?.toLowerCase() === site.toLowerCase(),
      );
    }

    this.exportTrailersToExcel(filteredTrailers, fileName);
  }

  /**
   * Export détaillé avec informations supplémentaires (taxes, etc.)
   */
  exportDetailedTrailers(
    trailers: any[],
    fileName: string = 'export_remorques_detaille',
  ): void {
    const data: any[][] = [];

    // En-têtes étendues
    data.push([
      'N° Remorque',
      'Transporteur',
      'Type Transport',
      'Plateforme',
      'Date Remorque',
      'Coût Transport International',
      'Taxes Marpol',
      'Surcharge Fuel',
      'Fournisseur',
      'Société',
      'Référence',
      'Date Enlèvement',
      'Volume Boîte (m³)',
      'Volume Palette (m³)',
      'Poids (Kg)',
      'Total',
      'Ancien Total',
    ]);

    trailers.forEach((trailer) => {
      // Ligne principale de la remorque
      data.push([
        trailer.remorqueId || '',
        trailer.transportor?.nameSociety || '',
        this.getTransportTypeLabel(trailer.transportorType),
        trailer.plateform || '',
        this.formatDate(trailer.creation_date),
        this.formatNumber(trailer.totalCost, 2),
        '', // Taxes Marpol (à ajouter selon vos données)
        '', // Surcharge Fuel (à ajouter selon vos données)
        '=== COMMANDES ===',
        null,
        null,
        null,
        null,
        this.formatNumber(trailer.totalVolume, 2),
        this.formatNumber(trailer.totalWeight, 1),
        this.formatNumber(trailer.totalCost, 2),
        '', // Ancien Total (à ajouter selon vos données)
      ]);

      // Commandes
      if (trailer.commands && trailer.commands.length > 0) {
        trailer.commands.forEach((cmd) => {
          data.push([
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            cmd.client_cmd?.providerName || '',
            cmd.society?.name || '',
            cmd.ref_facture_cmd || '',
            this.formatDate(cmd.pickupDate || cmd.date_creation_cmd),
            this.formatNumber(cmd.totalVolume, 3),
            this.formatNumber(cmd.totalPalletVolume, 2),
            this.formatNumber(cmd.totalPoids, 1),
            this.formatNumber(cmd.total_ca_cmd, 2),
            this.formatNumber(cmd.realTotalCaCmd, 2),
          ]);
        });
      }

      // Ligne vide
      data.push(new Array(17).fill(null));
    });

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    worksheet['!cols'] = [
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Remorques Détaillées');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, `${fileName}_${this.getCurrentDate()}.xlsx`);
  }

  // Fonctions utilitaires
  private formatDate(timestamp: any): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatNumber(
    value: number | null | undefined,
    decimals: number,
  ): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return value.toFixed(decimals).replace('.', ',');
  }

  private getTransportTypeLabel(type: string): string {
    const types: { [key: string]: string } = {
      complet: 'Complet',
      grouping: 'Groupage',
      partial: 'Partiel',
    };
    return types[type] || type;
  }

  private getCurrentDate(): string {
    const d = new Date();
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  }
}
